/**
 * 60. Lot / Serial Traceability — genealogy; stock movements; sources;
 * quality status.
 *
 * Arabic and English (C7): every word from the catalogs; a lot's quality
 * status is a word, never the stored value.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  Badge,
  Toolbar,
  Input,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { valueLabel } from "@/lib/i18n/records";
import { formatQuantity, formatDate } from "@/lib/money";

interface TraceabilityRow {
  id: string;
  lotNumber: string;
  serialNumber: string | null;
  productSku: string;
  productName: string;
  supplierName: string | null;
  quantity: string;
  sourceMovement: string | null;
  receivedAt: string;
  qualityStatus: "Passed" | "Quarantined" | "Failed";
}

export function TraceabilityPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<TraceabilityRow>(
    ["inventory", "traceability"],
    "/inventory/lots?trace=true",
  );

  return (
    <div>
      <PageHeader
        title={t("inventory.traceability.title")}
        subtitle={t("inventory.traceability.subtitle")}
        actions={
          <PendingButton variant="primary" reason="traceability" slice="MF-W5">
            {t("shared.export")}
          </PendingButton>
        }
      />
      <Toolbar>
        <Input
          type="text"
          placeholder={t("inventory.traceability.searchPlaceholder")}
          aria-label={t("inventory.traceability.searchLabel")}
        />
      </Toolbar>
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("inventory.traceability.list"), data.total)}>
          <DataTable<TraceabilityRow>
            rows={data.items}
            columns={[
              {
                key: "lotNumber",
                label: t("fields.lot"),
                render: (r) => (
                  <span className="atlas-mono">{r.lotNumber}</span>
                ),
              },
              {
                key: "serialNumber",
                label: t("inventory.traceability.serial"),
                render: (r) =>
                  r.serialNumber ? (
                    <span className="atlas-mono">{r.serialNumber}</span>
                  ) : (
                    "—"
                  ),
              },
              {
                key: "productSku",
                label: t("fields.sku"),
                render: (r) => (
                  <span className="atlas-mono">{r.productSku}</span>
                ),
              },
              { key: "productName", label: t("fields.product") },
              {
                key: "supplierName",
                label: t("fields.source"),
                render: (r) => r.supplierName ?? "—",
              },
              {
                key: "quantity",
                label: t("fields.qty"),
                numeric: true,
                render: (r) => formatQuantity(r.quantity),
              },
              {
                key: "receivedAt",
                label: t("fields.received"),
                render: (r) => formatDate(r.receivedAt),
              },
              {
                key: "qualityStatus",
                label: t("inventory.traceability.quality"),
                render: (r) =>
                  r.qualityStatus ? (
                    <Badge
                      tone={
                        r.qualityStatus === "Passed"
                          ? "success"
                          : r.qualityStatus === "Quarantined"
                            ? "warning"
                            : "danger"
                      }
                    >
                      {valueLabel(t, "qualityStatus", r.qualityStatus)}
                    </Badge>
                  ) : (
                    "—"
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
